import React, { useState, useRef, useEffect, useCallback } from 'react';
import MonacoEditor from '@monaco-editor/react';
import CanvasDisplay from './CanvasDisplay';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { sanitizeVisibleText } from './utils/sanitizeVisibleText';

const API_STREAM_URL = '/c-learning/api/stream-graphics';

const formatRichText = (text = '') =>
    text
        .replace(/\n/g, '<br>')
        .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>');

const GraphicsView = ({
    lesson,
    onBack,
    backLabel = 'レッスン一覧に戻る',
    contentTitle = 'レッスン内容',
    resetLabel = 'サンプルコードに戻す',
    autoRun = true,
    solutionCode = '',
    solutionLabel = '解答例',
    badges = []
}) => {
    const [code, setCode] = useState(lesson.code);
    const [isRunning, setIsRunning] = useState(false);
    const [error, setError] = useState('');
    const [textOutput, setTextOutput] = useState('');
    const [showSolution, setShowSolution] = useState(false);
    const [keyboardInput, setKeyboardInput] = useState('');
    const keyboardInputRef = useRef(keyboardInput);
    useEffect(() => { keyboardInputRef.current = keyboardInput; }, [keyboardInput]);
    const [frameCommands, setFrameCommands] = useState([]);
    
    const commandQueueRef = useRef([]);
    const abortCtrlRef    = useRef(null);
    const wsRef           = useRef(null);

    function sendKeyWithRetry(char) {
        const MAX_TRIES = 20;   // 最大リトライ回数
        let tries = 0;
        console.log("[sendKeyWithRetry] scheduling send:", char);   // log
        const interval = setInterval(() => {
        // readyState が OPEN なら送信して interval をクリア
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            console.log("[sendKeyWithRetry] WS open! sending char:", char); // log
            wsRef.current.send(char);
            clearInterval(interval);
        } else if (++tries >= MAX_TRIES) {
            console.error("WebSocket が開通せず送信に失敗しました:", char);
            console.error("[sendKeyWithRetry] WS never opened, give up on char:", char);   // log
            clearInterval(interval);
            }
        }, 10);  // 10ms ごとにチェック
    }

    const runCode = useCallback(() => {
        if (isRunning) return;

        setIsRunning(true);
        setError('');
        setTextOutput('');
        setFrameCommands([]);
        commandQueueRef.current = [];

        const ctrl = new AbortController();
        abortCtrlRef.current = ctrl;
        console.log("[runCode] starting SSE to:", API_STREAM_URL);  // log
        fetchEventSource(API_STREAM_URL, {
            method: 'POST',
            signal: ctrl.signal,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, input_data: "" }),
            
            onopen: async (response) => {
                console.log("[runCode] SSE onopen, status:", response.status);  // log
                if (!response.ok) {
                    setError(`❌ 接続エラー: サーバーがエラーを返しました (ステータス: ${response.status})`);
                    setIsRunning(false);
                    throw new Error('Failed to connect');
                }
            },
            onmessage(event) {
                const text = event.data;
                console.log("[SSE] raw event:", event); // log
                let msg;
                try {
                    msg = JSON.parse(text);
                    if (typeof msg !== 'object' || msg === null) {
                        throw new Error('プリミティブ');
                    }
                    console.log("[SSE] parsed msg:", msg);  // log
                } catch {
                    // printf出力などのテキストはそのまま出力
                    setTextOutput(prev => prev + sanitizeVisibleText(text) + '\n');
                    return;
                }
                
                // ① run_id のキャッチ
                if (msg.event === 'run_id') {
                    const runId = msg.data;
                    console.log("[SSE] got run_id:", runId);    // log
                    const proto = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
                    wsRef.current = new WebSocket(`${proto}${window.location.host}/c-learning/api/ws-graphics/${runId}`);
                    wsRef.current.onopen = () => console.log("[WS] open for run_id:", runId);
                    wsRef.current.onerror = (e) => console.error("[WS] error:", e); //log
                    wsRef.current.onclose = (e) => console.warn("[WS] close:", e);  // log 
                    return;
                }
                
                // ② コンパイル／実行エラー
                if (msg.event === 'error') {
                    setError(prev => prev + sanitizeVisibleText(msg.data || '') + '\n');
                    return;
                }
                
                // ③ get_key() 呼び出し (= request_key)
                if (msg.command === 'request_key') {
                    // キーボードシーケンスから一文字取り出す
                    const nextChar = keyboardInputRef.current.charAt(0) || '\n';
                    setKeyboardInput(prev => prev.slice(1));
                    keyboardInputRef.current = keyboardInputRef.current.slice(1);
                
                    // retry で送信
                    console.log("[SSE] request_key, nextChar:", nextChar);  // log
                    sendKeyWithRetry(nextChar);
                    return;
                }
                
                // ④ フレーム確定
                if (msg.command === 'flush') {
                    setFrameCommands([...commandQueueRef.current]);
                    commandQueueRef.current = [];
                    return;
                }
                
                // ⑤ 描画コマンド集積
                commandQueueRef.current.push(msg);
                },
            onerror(err) {
                if (err.event?.data) {
                    setError(prev => prev + sanitizeVisibleText(err.event.data) + '\n');
                }
                setIsRunning(false);
                throw err; // これでストリームが停止し、再接続を試みなくなります
            },
            onclose() {
                // サーバーがストリームを閉じたタイミングで必ず呼ばれる
                setIsRunning(false);
                wsRef.current?.close();
                if (commandQueueRef.current.length > 0) {
                    setFrameCommands([...commandQueueRef.current]);
                    commandQueueRef.current = [];
                }
            }
        });
    }, [code, keyboardInput, isRunning]);

    // 停止ボタンハンドラ
    const stopCode = () => {
        if (abortCtrlRef.current) {
            abortCtrlRef.current.abort();
            setIsRunning(false);
            wsRef.current?.close();
            // 残コマンドを最後に描画
            if (commandQueueRef.current.length) {
                setFrameCommands([...commandQueueRef.current]);
                commandQueueRef.current = [];
            }
        }
    };

    useEffect(() => {
        const onKeyDown = (e) => {
        if (isRunning && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(e.key);
        }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    }, [isRunning]);
    

    useEffect(() => {
        setCode(lesson.code);
        setShowSolution(false);
    }, [lesson.code]);

    useEffect(() => {
        // 初回マウント時のみ必要に応じてサンプルコードを実行
        if (autoRun && lesson.code) {
            runCode();
        }
    }, []); // 空の依存配列で初回のみ実行

    return (
        <div className="min-h-screen bg-gray-50">
            {/* ヘッダー */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <button onClick={onBack} className="flex items-center text-blue-600 hover:text-blue-800 mb-2">
                        ← {backLabel}
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">{lesson.title}</h1>
                    {badges.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
                            {badges.map((badge) => (
                                <span
                                    key={`${lesson.title}-${badge.label}`}
                                    className={badge.className}
                                >
                                    {badge.label}
                                </span>
                            ))}
                        </div>
                    )}
                    <p className="text-gray-600 mt-1">{lesson.description}</p>
                </div>
            </div>

            {/* コンテンツ */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 左側 */}
                    <div>
                        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                            <h2 className="text-xl font-semibold mb-4">{contentTitle}</h2>
                            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: formatRichText(lesson.content) }} />
                        </div>
                        <div className="border rounded-lg overflow-hidden mb-4 bg-gray-900">
                            <MonacoEditor height="300px" language="c" theme="vs-dark" value={code} onChange={(value) => setCode(value || '')} options={{ fontSize: 14, minimap: { enabled: false } }} />
                        </div>
                        <div className="mt-4">
                            <label htmlFor="keyboard-input" className="block text-sm font-medium text-gray-700">キーボード入力シーケンス</label>
                            <textarea
                                id="keyboard-input"
                                rows="2"
                                className="w-full p-2 mt-1 border rounded-md font-mono"
                                value={keyboardInput}
                                onChange={(e) => setKeyboardInput(e.target.value)}
                                placeholder="get_key()で順番に読み込ませたい文字を入力してください (例: wwssaadd)"
                            />
                        </div>
                         <div className="flex gap-2 mb-4 mt-4">
                            <button onClick={runCode} disabled={isRunning} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400">
                                {isRunning ? '実行中...' : '🚀 実行'}
                            </button>
                            <button onClick={stopCode} disabled={!isRunning} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400">
                                ⏹️ 停止
                            </button>
                            <button onClick={() => setCode(lesson.code)} disabled={isRunning} className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:bg-gray-400">
                                🔄 {resetLabel}
                            </button>
                         </div>
                         {solutionCode && (
                            <div className="bg-white rounded-lg shadow-sm border p-4">
                                <button
                                    onClick={() => setShowSolution(!showSolution)}
                                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                                >
                                    {showSolution ? '解答を隠す' : '💡 解答を見る'}
                                </button>
                                {showSolution && (
                                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                                        <h3 className="font-semibold mb-2">{solutionLabel}:</h3>
                                        <pre className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                            {solutionCode}
                                        </pre>
                                    </div>
                                )}
                            </div>
                         )}
                    </div>

                    {/* 右側 */}
                    <div>
                        <h2 className="text-xl font-semibold mb-4">描画キャンバス</h2>
                        <CanvasDisplay commands={frameCommands} />
                        {textOutput && (
                            <div className="mt-4"><h3 className="text-sm font-semibold text-gray-800 mb-2">テキスト出力 (printf)</h3><pre className="p-3 bg-gray-900 text-white rounded-lg font-mono text-xs whitespace-pre-wrap h-24 overflow-y-auto">{textOutput}</pre></div>
                        )}
                        {error && (
                            <div className="mt-4 p-4 bg-red-900 text-red-300 rounded-lg font-mono text-sm"><h3 className="font-semibold mb-2">エラー:</h3><pre className="whitespace-pre-wrap">{error}</pre></div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GraphicsView;
